import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Loader2,
  Plus,
  Pencil,
  Trash2,
  CheckCircle2,
  XCircle,
  Send,
  Eye,
  Download,
  Sparkles,
  Filter,
  ArrowRight,
  FileCheck2,
} from 'lucide-react';
import {
  AuditAction,
  AuditLogEntry,
  useProjectAuditLog,
  useProjectMembers,
  useProjectMembership,
} from '../../hooks/useProjectMembership';
import { ACCESS_ROLE_LABELS, AccessRole } from '../../lib/permissions';
import { Lock } from 'lucide-react';
import { httpsCallable } from '../../lib/callable';
import { saveAs } from 'file-saver';
import { functions } from '../../firebase';
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from '../../constants/firebase';
import {
  auditLogsToCsv,
  auditTimestampToDate,
  type AuditCsvEntry,
} from '../../lib/auditExport';

interface ActionMeta {
  label: string;
  badgeClass: string;
  iconClass: string;
  icon: React.ReactNode;
}

const ACTION_META: Record<AuditAction, ActionMeta> = {
  create: {
    label: 'Created',
    badgeClass: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    iconClass: 'text-emerald-600',
    icon: <Plus className="h-4 w-4" />,
  },
  update: {
    label: 'Updated',
    badgeClass: 'bg-blue-50 text-blue-700 ring-blue-200',
    iconClass: 'text-blue-600',
    icon: <Pencil className="h-4 w-4" />,
  },
  delete: {
    label: 'Deleted',
    badgeClass: 'bg-red-50 text-red-700 ring-red-200',
    iconClass: 'text-red-600',
    icon: <Trash2 className="h-4 w-4" />,
  },
  approve: {
    label: 'Approved',
    badgeClass: 'bg-violet-50 text-violet-700 ring-violet-200',
    iconClass: 'text-violet-600',
    icon: <CheckCircle2 className="h-4 w-4" />,
  },
  reject: {
    label: 'Rejected',
    badgeClass: 'bg-amber-50 text-amber-700 ring-amber-200',
    iconClass: 'text-amber-600',
    icon: <XCircle className="h-4 w-4" />,
  },
  submit_for_review: {
    label: 'Submitted',
    badgeClass: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
    iconClass: 'text-indigo-600',
    icon: <Send className="h-4 w-4" />,
  },
  view: {
    label: 'Viewed',
    badgeClass: 'bg-slate-50 text-slate-700 ring-slate-200',
    iconClass: 'text-slate-500',
    icon: <Eye className="h-4 w-4" />,
  },
  export: {
    label: 'Exported',
    badgeClass: 'bg-sky-50 text-sky-700 ring-sky-200',
    iconClass: 'text-sky-600',
    icon: <Download className="h-4 w-4" />,
  },
  agent_action: {
    label: 'Generated',
    badgeClass: 'bg-purple-50 text-purple-700 ring-purple-200',
    iconClass: 'text-purple-600',
    icon: <Sparkles className="h-4 w-4" />,
  },
  issue: {
    label: 'Issued',
    badgeClass: 'bg-teal-50 text-teal-700 ring-teal-200',
    iconClass: 'text-teal-600',
    icon: <FileCheck2 className="h-4 w-4" />,
  },
};

type DateRange = 'today' | '7d' | '30d' | 'all';

const DATE_RANGE_OPTIONS: Array<{ value: DateRange; label: string }> = [
  { value: 'today', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'all', label: 'All time' },
];

// Entries now arrive via the getAuditLogsV2 callable, where an Admin
// Timestamp is serialised to { _seconds, _nanoseconds } (no .toDate()).
// auditTimestampToDate normalises every shape we might see.
function tsToDate(value: unknown): Date | null {
  return auditTimestampToDate(value);
}

function formatAbsolute(date: Date | null): string {
  if (!date) return '';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function withinRange(date: Date | null, range: DateRange): boolean {
  if (range === 'all') return true;
  if (!date) return false;
  const now = Date.now();
  const cutoff =
    range === 'today'
      ? now - 24 * 60 * 60 * 1000
      : range === '7d'
      ? now - 7 * 24 * 60 * 60 * 1000
      : now - 30 * 24 * 60 * 60 * 1000;
  return date.getTime() >= cutoff;
}

// Lower bound (ISO) for the active date range, or undefined for "all time".
// Passed to the export callable so the CSV matches what's on screen.
function rangeStartIso(range: DateRange): string | undefined {
  if (range === 'all') return undefined;
  const now = Date.now();
  const ms =
    range === 'today'
      ? 24 * 60 * 60 * 1000
      : range === '7d'
      ? 7 * 24 * 60 * 60 * 1000
      : 30 * 24 * 60 * 60 * 1000;
  return new Date(now - ms).toISOString();
}

function groupKey(date: Date | null): string {
  if (!date) return '—';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  if (target.getTime() === today.getTime()) return 'Today';
  if (target.getTime() === yesterday.getTime()) return 'Yesterday';
  return target.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    year:
      target.getFullYear() === today.getFullYear() ? undefined : 'numeric',
  });
}

export default function Activity() {
  const { projectId } = useParams<{ projectId: string }>();
  // Activity is gated by the dedicated view_activity privilege so the
  // Owner can grant audit-log access to a specific member from the
  // Team table without having to promote them to Manager. Default
  // grants: Owner + Manager only.
  const { has: hasPriv, loading: privLoading } = useProjectMembership(projectId);
  const canViewActivity = hasPriv('view_activity');
  const allowed = canViewActivity || privLoading; // optimistic during load
  const { entries, loading, loadingMore, error, hasMore, loadMore } =
    useProjectAuditLog(allowed ? projectId : undefined);
  // Used to translate member UIDs in stored descriptions / resource IDs
  // into human-friendly emails. Old audit entries are immutable so they
  // still contain raw UIDs in the description text — we patch them up at
  // render time.
  const { members } = useProjectMembers(allowed ? projectId : undefined);

  const [actionFilter, setActionFilter] = useState<'all' | AuditAction>('all');
  const [userFilter, setUserFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<DateRange>('7d');
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // Download the activity log as CSV for the active date range. Pulls the
  // full window from the server (not just the on-screen entries) so the
  // export is complete, then flattens to CSV client-side.
  async function handleExport() {
    if (!projectId || exporting) return;
    setExporting(true);
    setExportError(null);
    try {
      const fn = httpsCallable(functions, CLOUD_FUNCTIONS.EXPORT_AUDIT_LOGS, { timeout: CALLABLE_TIMEOUTS.SHORT });
      const res = await fn({ projectId, startDate: rangeStartIso(dateRange) });
      const logs = ((res.data as { logs?: AuditCsvEntry[] })?.logs ??
        []) as AuditCsvEntry[];
      const stamp = new Date().toISOString().slice(0, 10);
      saveAs(
        new Blob([auditLogsToCsv(logs)], { type: 'text/csv;charset=utf-8' }),
        `activity_${projectId}_${stamp}.csv`
      );
    } catch (e) {
      setExportError((e as Error).message || 'Could not export the activity log.');
    } finally {
      setExporting(false);
    }
  }

  // uid -> "email | displayName | uid" used for description fix-up.
  const uidLabel = useMemo(() => {
    const m = new Map<string, string>();
    for (const member of members) {
      const label = member.email || member.displayName || member.userId;
      m.set(member.userId, label);
    }
    return m;
  }, [members]);

  // Distinct users in the loaded window — drives the user dropdown.
  const usersInLog = useMemo(() => {
    const seen = new Map<string, string>();
    for (const e of entries) {
      if (!seen.has(e.userId)) {
        seen.set(
          e.userId,
          uidLabel.get(e.userId) || e.userName || e.userEmail || e.userId
        );
      }
    }
    return Array.from(seen.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [entries, uidLabel]);

  /**
   * Rewrites legacy audit-log descriptions on the fly:
   *   - Replaces any occurrence of a known member UID with their email.
   *   - For member-update entries that captured previousValue / newValue,
   *     synthesises a clean "Changed X's role from Y to Z." description
   *     (or analogous language for override-only updates), so old
   *     "Updated member <uid> (role=...)" rows render nicely too.
   * Returns null when no override is needed — caller falls back to the
   * stored description.
   */
  function rewriteDescription(entry: AuditLogEntry): string | null {
    // Member-update synthesis — only when we have a target uid + role
    // change captured in previousValue/newValue.
    const prevRole = (entry.previousValue?.accessRole as
      | AccessRole
      | undefined) ?? undefined;
    const newRole = (entry.newValue?.accessRole as AccessRole | undefined) ??
      undefined;
    const targetUid = entry.resourceId;
    const targetLabel =
      (targetUid && uidLabel.get(targetUid)) || targetUid || '';

    const isMemberUpdate =
      entry.action === 'update' &&
      entry.resourceType === 'agent' &&
      !!targetUid &&
      uidLabel.has(targetUid);

    if (isMemberUpdate && prevRole && newRole && prevRole !== newRole) {
      return `Changed ${targetLabel}'s role from ${ACCESS_ROLE_LABELS[prevRole]} to ${ACCESS_ROLE_LABELS[newRole]}.`;
    }
    if (isMemberUpdate && newRole && !prevRole) {
      return `Set ${targetLabel}'s role to ${ACCESS_ROLE_LABELS[newRole]}.`;
    }
    if (
      isMemberUpdate &&
      entry.newValue?.permissionOverrides !== undefined &&
      !newRole
    ) {
      const overrides = entry.newValue.permissionOverrides as Record<
        string,
        boolean
      >;
      const count = Object.keys(overrides || {}).length;
      return count === 0
        ? `Cleared privilege overrides for ${targetLabel}.`
        : `Updated ${count} privilege override${count === 1 ? '' : 's'} for ${targetLabel}.`;
    }

    // Generic UID -> email substitution in the stored description.
    let out = entry.description;
    let didReplace = false;
    for (const [uid, label] of uidLabel) {
      if (out.includes(uid)) {
        out = out.split(uid).join(label);
        didReplace = true;
      }
    }
    return didReplace ? out : null;
  }

  // Apply filters client-side. With the 200-entry cap this stays cheap.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entries.filter(e => {
      if (actionFilter !== 'all' && e.action !== actionFilter) return false;
      if (userFilter !== 'all' && e.userId !== userFilter) return false;
      const date = tsToDate(e.timestamp);
      if (!withinRange(date, dateRange)) return false;
      if (q) {
        const hay = (
          e.description +
          ' ' +
          (e.userName || '') +
          ' ' +
          (e.userEmail || '') +
          ' ' +
          (e.resourceType || '') +
          ' ' +
          (e.resourceId || '')
        ).toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [entries, actionFilter, userFilter, dateRange, search]);

  // Group filtered entries by day for visual scanning.
  const grouped = useMemo(() => {
    const groups: Array<{ key: string; entries: AuditLogEntry[] }> = [];
    let lastKey: string | null = null;
    let bucket: AuditLogEntry[] | null = null;
    for (const e of filtered) {
      const key = groupKey(tsToDate(e.timestamp));
      if (key !== lastKey) {
        bucket = [];
        groups.push({ key, entries: bucket });
        lastKey = key;
      }
      bucket!.push(e);
    }
    return groups;
  }, [filtered]);

  // Privilege gate. Don't even render the filter bar / feed if the
  // caller can't see Activity — the sidebar entry is also hidden, but
  // someone with the URL could navigate here directly.
  if (!privLoading && !canViewActivity) {
    return (
      <div className="max-w-3xl">
        <header className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">Activity</h1>
        </header>
        <div className="rounded-xl ring-1 ring-gray-200 bg-white p-10 text-center">
          <Lock className="h-8 w-8 text-gray-400 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-900">
            Activity is restricted to members with audit-log access.
          </p>
          <p className="text-xs text-gray-500 mt-1.5 max-w-md mx-auto">
            Your role doesn't include the Activity privilege. Ask the
            project Owner to grant Activity access on the Team page if
            you need to see the change-history feed.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Activity</h1>
          <p className="mt-1 text-sm text-gray-500">
            Chronological record of changes on this project &mdash; who did
            what, when. Read-only and immutable.
          </p>
        </div>
        {canViewActivity && (
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-lg ring-1 ring-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {exporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {exporting ? 'Exporting…' : 'Export activity log'}
          </button>
        )}
      </header>

      {exportError && (
        <div className="mb-4 rounded-lg bg-red-50 ring-1 ring-red-200 px-4 py-2 text-sm text-red-800">
          {exportError}
        </div>
      )}

      {/* Filter bar */}
      <div className="mb-6 flex flex-wrap items-center gap-2">
        <input
          type="search"
          placeholder="Search description, resource, person…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1 min-w-[220px] rounded-lg ring-1 ring-gray-200 focus:ring-2 focus:ring-primary-500 focus:outline-none px-3 py-2 text-sm"
        />
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <Filter className="h-3.5 w-3.5" />
          <span>Filters:</span>
        </div>
        <select
          value={dateRange}
          onChange={e => setDateRange(e.target.value as DateRange)}
          className="rounded-lg ring-1 ring-gray-200 px-2.5 py-2 text-sm bg-white"
        >
          {DATE_RANGE_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          value={userFilter}
          onChange={e => setUserFilter(e.target.value)}
          className="rounded-lg ring-1 ring-gray-200 px-2.5 py-2 text-sm bg-white"
        >
          <option value="all">All people</option>
          {usersInLog.map(u => (
            <option key={u.id} value={u.id}>
              {u.label}
            </option>
          ))}
        </select>
        <select
          value={actionFilter}
          onChange={e => setActionFilter(e.target.value as 'all' | AuditAction)}
          className="rounded-lg ring-1 ring-gray-200 px-2.5 py-2 text-sm bg-white"
        >
          <option value="all">All actions</option>
          {(Object.keys(ACTION_META) as AuditAction[]).map(a => (
            <option key={a} value={a}>
              {ACTION_META[a].label}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
        </div>
      ) : error ? (
        <div className="rounded-lg bg-red-50 ring-1 ring-red-200 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl ring-1 ring-gray-200 bg-white p-10 text-center">
          <p className="text-sm text-gray-500">
            {entries.length === 0
              ? 'No activity recorded on this project yet.'
              : 'No entries match the current filters.'}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map(group => (
            <section key={group.key}>
              <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2 sticky top-0 bg-gray-50/95 backdrop-blur py-1 z-10">
                {group.key}
              </h2>
              <ul className="rounded-xl ring-1 ring-gray-200 bg-white divide-y divide-gray-100 overflow-hidden">
                {group.entries.map(entry => {
                  const meta = ACTION_META[entry.action] || ACTION_META.update;
                  const date = tsToDate(entry.timestamp);
                  const personLabel =
                    uidLabel.get(entry.userId) ||
                    entry.userName ||
                    entry.userEmail ||
                    entry.userId;
                  // Resource id shown in the small grey crumb: replace
                  // member-uid resource ids with the email when known.
                  const resourceLabel =
                    entry.resourceId && uidLabel.has(entry.resourceId)
                      ? uidLabel.get(entry.resourceId)
                      : entry.resourceId;
                  // Description: synthesised from previousValue/newValue
                  // for member updates, or UID-substituted otherwise.
                  // Falls back to the stored description.
                  const description =
                    rewriteDescription(entry) || entry.description;
                  // Before / after role badges, when we have both.
                  const prevRole = entry.previousValue?.accessRole as
                    | AccessRole
                    | undefined;
                  const newRole = entry.newValue?.accessRole as
                    | AccessRole
                    | undefined;
                  const showRoleDelta =
                    prevRole && newRole && prevRole !== newRole;

                  return (
                    <li
                      key={entry.id}
                      className="px-4 py-3 flex items-start gap-3 hover:bg-gray-50/40 transition-colors"
                    >
                      <div
                        className={`shrink-0 mt-0.5 h-7 w-7 rounded-full bg-white ring-1 ring-gray-200 flex items-center justify-center ${meta.iconClass}`}
                        aria-hidden
                      >
                        {meta.icon}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="font-medium text-gray-900 text-sm">
                            {personLabel}
                          </span>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ring-1 ring-inset ${meta.badgeClass}`}
                          >
                            {meta.label}
                          </span>
                          {entry.resourceType && (
                            <span className="text-[11px] text-gray-400">
                              {entry.resourceType}
                              {resourceLabel ? ` · ${resourceLabel}` : ''}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-700 mt-0.5">
                          {description}
                        </p>
                        {showRoleDelta && (
                          <div className="mt-1 inline-flex items-center gap-1.5 text-[11px]">
                            <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 ring-1 ring-gray-200">
                              {ACCESS_ROLE_LABELS[prevRole!]}
                            </span>
                            <ArrowRight className="h-3 w-3 text-gray-400" />
                            <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
                              {ACCESS_ROLE_LABELS[newRole!]}
                            </span>
                          </div>
                        )}
                      </div>
                      <time
                        className="shrink-0 text-xs text-gray-400 tabular-nums whitespace-nowrap"
                        dateTime={date ? date.toISOString() : undefined}
                      >
                        {formatAbsolute(date)}
                      </time>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}

      {!loading && !error && hasMore && (
        <div className="mt-6 flex justify-center">
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            className="inline-flex items-center gap-1.5 rounded-lg ring-1 ring-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loadingMore && <Loader2 className="h-4 w-4 animate-spin" />}
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}

      <p className="mt-6 text-xs text-gray-400">
        Showing {filtered.length}
        {filtered.length !== entries.length
          ? ` of ${entries.length} loaded`
          : ''}{' '}
        {entries.length === 1 ? 'entry' : 'entries'}
        {hasMore ? ' — load more to page further back' : ''}. The full history
        is retained server-side and remains immutable.
      </p>
    </div>
  );
}
